/**
 * pages/signup/signup-store.ts — web signup progress + retention seam.
 *
 * The web signup mirrors the mobile onboarding: the user completes a
 * fixed-order sequence of forms; a mid-flow refresh lands them back
 * exactly where they were. The persistence layer is sessionStorage
 * (NOT localStorage): a browser-close is a signal we want to respect —
 * "start fresh" is the more forgiving default than "resume that
 * abandoned session from last month".
 *
 * SSN retention: same as the mobile store. The full SSN is passed
 * through the SSN screen's local component state; it is NEVER handed
 * to `setProgress`. If a future change tries to persist it, the shared
 * `stripBannedFields` seam strips it at write time and this file's
 * dev-only warning fires.
 */

import {
  BANNED_PROGRESS_FIELDS,
  stripBannedFields,
  type OnboardingProgress,
  type OnboardingStep,
} from "@aster/onboarding";

// The web signup uses the SAME OnboardingProgress shape as mobile.
// Anything shared onboarding logic learns about resume rules, tier
// bump, or retention semantics applies verbatim on the web.
export type SignupProgress = OnboardingProgress;
export type SignupStep = OnboardingStep;

const KEY = "aster.web.signup.progress";
const IS_DEV = import.meta.env.DEV;

// ---------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------

/** Returns null if no stored progress OR the storage is unavailable
 * (e.g. Safari private mode with sessionStorage disabled — degrade
 * gracefully, don't crash). */
export function getProgress(): SignupProgress | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    if (!parsed.step || !parsed.written_at) return null;
    return parsed as SignupProgress;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------
// Write — the retention seam.
//
// EVERY write goes through this function. It strips banned fields at
// the seam, which is why the SSN can never sit on disk even if a
// future refactor invents a `progress.ssn` field. Callers writing
// through `setProgress` cannot bypass this.
// ---------------------------------------------------------------------

export function setProgress(next: SignupProgress): void {
  if (typeof window === "undefined") return;
  const { safe, stripped } = stripBannedFields(
    next as unknown as Record<string, unknown>,
  );
  if (stripped.length > 0 && IS_DEV) {
    // eslint-disable-next-line no-console
    console.warn(
      `[signup-store] refused to persist banned fields: ${stripped.join(", ")}`,
    );
  }
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(safe));
  } catch {
    // Full storage / quota exceeded — degrade silently. The user
    // will complete the flow with client-only state instead.
  }
}

// ---------------------------------------------------------------------
// Clear — used on:
//   - signup complete (verified),
//   - `blocked` terminal (user unwinds; server has the audit trail),
//   - explicit "start over" action if we add one.
// ---------------------------------------------------------------------

export function clearProgress(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Ignore.
  }
}

/**
 * Return the SIGNUP_STEPS enum — kept here so the routing table
 * (Signup.tsx) can pin down the ORDER without importing the full
 * OnboardingStep type (which includes phone/otp steps the web doesn't
 * currently render; sign-in handles those).
 */
export const SIGNUP_STEPS: readonly SignupStep[] = [
  "legal",
  "name",
  "dob",
  "address",
  "ssn",
  "verifying",
  "pending",
  "documents",
  "rejected",
  "blocked",
] as const;

/** Every field this store is capable of clobbering. Re-exported for
 * documentation and to keep the invariant test's list stable. */
export const _BANNED_PROGRESS_FIELDS = BANNED_PROGRESS_FIELDS;
